import { useState } from "react";
import { t } from "ttag";

import { getErrorMessage } from "metabase/api/utils/errors";
import { ExternalLink } from "metabase/common/components/ExternalLink";
import { useSelector } from "metabase/redux";
import { getApplicationName } from "metabase/selectors/whitelabel";
import {
  Alert,
  Button,
  Group,
  Icon,
  Stack,
  Text,
  TextInput,
} from "metabase/ui";
import type { GitSyncSettings } from "metabase-types/api";

import { useCreateGitHubAppManifestMutation } from "../api";

/**
 * Sends the browser to GitHub with the App's manifest. GitHub only takes a
 * manifest as a form field posted by the browser.
 */
export function postManifestToGitHub(url: string, manifest: string) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  form.hidden = true;
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "manifest";
  input.value = manifest;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

type ConnectGitHubAppProps = {
  settings: GitSyncSettings;
  /** Called just before the browser leaves for GitHub. */
  onLeave: () => void;
};

/**
 * Creates a GitHub App for Git sync through GitHub's manifest flow and
 * installs it: GitHub hands the App's ID, private key and installation ID to
 * Metabase, so nobody copies a key.
 */
export function ConnectGitHubApp({ settings, onLeave }: ConnectGitHubAppProps) {
  const applicationName = useSelector(getApplicationName);
  const [organization, setOrganization] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  const [createManifest, { isLoading, error }] =
    useCreateGitHubAppManifestMutation();
  const isSetByEnv = settings.github_app_env_vars.length > 0;

  const handleConnect = async () => {
    const response = await createManifest({
      organization: organization.trim() || undefined,
      github_url: githubUrl.trim() || undefined,
    });
    if (response.data) {
      onLeave();
      postManifestToGitHub(response.data.url, response.data.manifest);
    }
  };

  return (
    <Stack gap="sm">
      <Text c="text-secondary">
        {t`${applicationName} creates a GitHub App that can read and push to the repository, and you install it on the repository. GitHub hands its private key to ${applicationName} directly.`}
      </Text>
      <Group grow align="flex-start">
        <TextInput
          label={t`Organization`}
          description={t`The GitHub organization that owns the repository. Leave it empty for your own account.`}
          value={organization}
          onChange={(event) => setOrganization(event.target.value)}
          disabled={isSetByEnv}
        />
        <TextInput
          label={t`GitHub Enterprise Server address`}
          description={t`Only for GitHub Enterprise Server. Leave it empty for github.com.`}
          placeholder="https://github.example.com"
          value={githubUrl}
          onChange={(event) => setGithubUrl(event.target.value)}
          disabled={isSetByEnv}
        />
      </Group>
      <Group gap="md">
        <Button
          variant="default"
          leftSection={<Icon name="external" />}
          loading={isLoading}
          disabled={isSetByEnv}
          onClick={handleConnect}
        >
          {settings.github_app_url
            ? t`Connect another GitHub App`
            : t`Connect GitHub`}
        </Button>
        {settings.github_app_url && (
          <ExternalLink href={`${settings.github_app_url}/installations/new`}>
            {t`Change repositories`}
          </ExternalLink>
        )}
      </Group>
      {error != null && (
        <Alert color="error" icon={<Icon name="warning" />}>
          {getErrorMessage(error)}
        </Alert>
      )}
    </Stack>
  );
}
